/**
 * The public account-deletion page's client (X-03, W8-PRIVACY-WORKER, D439 — a Google Play requirement: a web page where
 * anyone can ask for their account to be deleted without the app). Two calls, both on the api (no session, no cookie):
 *
 *  1. `POST /v1/auth/otp/request` — the EXISTING OTP send (`{ phoneE164, realm: 'RIDER', locale? }`). Its 202 is the same
 *     for an existing account and a new phone (anti-enumeration); it carries the CURRENT `challengeId` (the id the code is
 *     verified against) and a display-only `maskedDestination`.
 *  2. `POST /v1/privacy/public/deletion-requests` — `{ challengeId, code }`. The api consumes the code WITHOUT signing in
 *     or creating an account and records an erasure for the phone's account, if one exists. The answer is the SAME 202
 *     `{ status: 'RECEIVED' }` either way, so the page shows ONE confirmation and never branches on the body.
 *
 * Every refusal maps to one of a few outcomes the page can say truthfully. The OTP send may ask for a human challenge
 * (`429 OTP_CHALLENGE_REQUIRED`, D383 proof-of-work): the site does not solve it (it would need `@vello/core`'s solver); the
 * page says "try again later" instead — the phone's budget refills on the server's own schedule.
 *
 * Pure and framework-neutral: `fetch` is injected (default: the global one, looked up per call), no DOM, no market literal.
 */

export const OTP_REQUEST_PATH = '/v1/auth/otp/request';
export const PUBLIC_DELETION_PATH = '/v1/privacy/public/deletion-requests';

/** The slice of `fetch` this client uses (structural, so tests script it without a DOM). */
export type FetchLike = (
  url: string,
  init: { readonly method: 'POST'; readonly headers: Readonly<Record<string, string>>; readonly body: string; readonly credentials: 'omit' },
) => Promise<{ readonly status: number; json(): Promise<unknown> }>;

export type SendCodeOutcome =
  | { readonly kind: 'sent'; readonly challengeId: string; readonly maskedDestination?: string }
  /** The api refused the number's shape (a 400 other than "country not served"). */
  | { readonly kind: 'invalid-phone' }
  /** Too many sends for this phone / device / network (429 with a wait). */
  | { readonly kind: 'rate-limited' }
  /** The phone's last anti-abuse token needs a human challenge this page does not solve (429 OTP_CHALLENGE_REQUIRED). */
  | { readonly kind: 'challenge-required' }
  /** Codes are not sent to numbers of this country (400 OTP_COUNTRY_NOT_SERVED). */
  | { readonly kind: 'not-served' }
  | { readonly kind: 'unavailable' };

export type ConfirmOutcome =
  /** The request was received — the same answer whether or not the phone had an account. */
  | { readonly kind: 'received' }
  /** Wrong, expired or already used code (401), or a malformed one (400): the visitor may try again. */
  | { readonly kind: 'wrong-code' }
  | { readonly kind: 'rate-limited' }
  | { readonly kind: 'unavailable' };

export interface AccountDeletionClient {
  requestCode(input: { readonly phoneE164: string; readonly locale?: string }): Promise<SendCodeOutcome>;
  confirm(input: { readonly challengeId: string; readonly code: string }): Promise<ConfirmOutcome>;
}

const E164 = /^\+[1-9]\d{1,14}$/;

/**
 * The E.164 form of what the visitor typed, or undefined. Spaces, dashes, dots and parentheses are dropped; the number
 * must start with `+` and its country calling code (the page never assumes one — neutral).
 */
export function normalizePhoneE164(raw: string): string | undefined {
  const compact = raw.trim().replace(/[\s().-]/g, '');
  return E164.test(compact) ? compact : undefined;
}

/** The code as typed, without the spaces a paste may carry. */
export function normalizeOtpCode(raw: string): string {
  return raw.replace(/\s/g, '');
}

type Posted = { readonly status: number; readonly body: unknown } | undefined;

const record = (v: unknown): Readonly<Record<string, unknown>> | undefined =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;

export function createAccountDeletionClient(deps: { readonly baseUrl: string; readonly fetch?: FetchLike }): AccountDeletionClient {
  const base = deps.baseUrl.replace(/\/+$/, '');
  const doFetch: FetchLike = deps.fetch ?? ((url, init) => globalThis.fetch(url, init));

  /** POST a JSON body; undefined on a network failure. The body is read fail-soft (a 202 may carry none). */
  const post = async (path: string, body: unknown): Promise<Posted> => {
    try {
      const res = await doFetch(`${base}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
        credentials: 'omit',
      });
      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        parsed = undefined;
      }
      return { status: res.status, body: parsed };
    } catch {
      return undefined;
    }
  };

  return {
    async requestCode({ phoneE164, locale }) {
      const res = await post(OTP_REQUEST_PATH, { phoneE164, realm: 'RIDER', ...(locale !== undefined && locale !== '' ? { locale } : {}) });
      if (res === undefined) return { kind: 'unavailable' };
      const body = record(res.body);
      if (res.status === 202) {
        const challengeId = body?.['challengeId'];
        if (typeof challengeId !== 'string' || challengeId === '') return { kind: 'unavailable' };
        const masked = body?.['maskedDestination'];
        return { kind: 'sent', challengeId, ...(typeof masked === 'string' && masked !== '' ? { maskedDestination: masked } : {}) };
      }
      const reasonCode = body?.['reasonCode'];
      if (res.status === 429) return reasonCode === 'OTP_CHALLENGE_REQUIRED' ? { kind: 'challenge-required' } : { kind: 'rate-limited' };
      if (res.status === 400) return reasonCode === 'OTP_COUNTRY_NOT_SERVED' ? { kind: 'not-served' } : { kind: 'invalid-phone' };
      return { kind: 'unavailable' };
    },

    async confirm({ challengeId, code }) {
      const res = await post(PUBLIC_DELETION_PATH, { challengeId, code });
      if (res === undefined) return { kind: 'unavailable' };
      // The body is deliberately NOT read: the answer is the same whether or not an account existed.
      if (res.status === 202) return { kind: 'received' };
      if (res.status === 401 || res.status === 400) return { kind: 'wrong-code' };
      if (res.status === 429) return { kind: 'rate-limited' };
      return { kind: 'unavailable' };
    },
  };
}
